import { NotificationItem } from "./NotificationItem";
import { useNotificationStore } from "../../stores/notification-store";
import {
  useNotifications,
  useMarkAllNotificationsRead,
} from "../../hooks/useNotifications";
import { EmptyState } from "../ui/EmptyState";
import { Spinner } from "../ui/Spinner";
import BellLinear from "~icons/solar/bell-linear";

interface NotificationDropdownProps {
  onClose: () => void;
}

export function NotificationDropdown({ onClose }: NotificationDropdownProps) {
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const recentNotifications = useNotificationStore(
    (s) => s.recentNotifications
  );

  const { isLoading } = useNotifications({ limit: 20 });
  const { mutate: markAllRead, isPending: isMarkingAll } =
    useMarkAllNotificationsRead();

  return (
    <div
      className="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-2xl border border-black/10 bg-white shadow-pop motion-safe:animate-dropdown-slide-in sm:w-96"
      role="dialog"
      aria-label="Notifications"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-black/[0.06] px-4 py-3">
        <h2 className="text-sm font-bold text-ink">Notifications</h2>
        {unreadCount > 0 && (
          <button
            type="button"
            className="rounded-md text-xs font-semibold text-primary transition-colors hover:text-primary-hover hover:underline disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            onClick={() => markAllRead()}
            disabled={isMarkingAll}
          >
            Mark all read
          </button>
        )}
      </div>

      {/* Body */}
      <div className="max-h-[400px] overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Spinner size={24} label="Loading notifications" />
          </div>
        ) : recentNotifications.length === 0 ? (
          <EmptyState
            compact
            icon={BellLinear}
            title="No notifications yet"
            description="You're all caught up."
          />
        ) : (
          <ul role="list" className="divide-y divide-black/[0.05]">
            {recentNotifications.map((notification) => (
              <li key={notification.id}>
                <NotificationItem
                  notification={notification}
                  onClose={onClose}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
